import React from 'react';
import { Box, Button, Chip, IconButton, InputAdornment, Paper, Skeleton, Stack, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const surfaceSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  overflow: 'hidden',
  boxShadow: 'none',
  bgcolor: 'background.paper',
};

/** Common surface only; the owning Default/Custom layout supplies all table behavior. */
export function TableSurface({ children, sx, ...props }) {
  return <Paper elevation={0} sx={{ ...surfaceSx, ...sx }} {...props}>{children}</Paper>;
}

/** Shared toolbar geometry with intentionally slot-based content. */
export function TableToolbar({ children, sx, ...props }) {
  return <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, px: { xs: 1.5, md: 2 }, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', ...sx }} {...props}>{children}</Box>;
}

export function TableHeader({ children, sx, ...props }) {
  return <TableHead sx={{ '& th': TABLE_HEADER_CELL_SX, ...sx }} {...props}>{children}</TableHead>;
}

export function TableSearch({ value, onChange, label = 'Search records', placeholder, onSubmit, sx, ...props }) {
  return <TextField size="small" label={label} placeholder={placeholder} value={value} onChange={onChange} onKeyDown={(event) => { if (event.key === 'Enter') onSubmit?.(event); }} sx={sx} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment> }} {...props} />;
}

export function TableActionButton({ children, label, ...props }) {
  return <Button size="small" aria-label={label} title={label} {...props}>{children}</Button>;
}

export function TableCopyButton({ label = 'Copy identifier', children, ...props }) {
  return <IconButton size="small" aria-label={label} title={label} {...props}>{children}</IconButton>;
}

export function TableRecordCount({ count, label = 'records', sx }) {
  return <Chip size="small" variant="outlined" color="primary" label={`${Number(count || 0).toLocaleString()} ${label}`} sx={{ fontWeight: 650, ...sx }} />;
}

export function TableStatusBadge({ status, label, color = 'default', variant }) {
  return <Chip size="small" label={label || status || 'Unknown'} color={color} variant={variant || (color === 'default' ? 'outlined' : 'filled')} />;
}

export function TableLoadingState({ rows = 5, columns = 1, label = 'Loading table data' }) {
  return <Box role="status" aria-label={label} className="space-y-2 p-4">{Array.from({ length: rows }, (_, row) => <Stack key={row} direction="row" spacing={2}>{Array.from({ length: columns }, (_, column) => <Skeleton key={column} height={34} sx={{ flex: 1 }} />)}</Stack>)}</Box>;
}

export function TableSkeletonRows({ rows = 5, columns = 1 }) {
  return <>{Array.from({ length: rows }, (_, row) => <TableRow key={`skeleton-${row}`} aria-hidden="true">{Array.from({ length: columns }, (_, column) => <TableCell key={column}><Skeleton animation="wave" /></TableCell>)}</TableRow>)}</>;
}

export function TableEmptyState({ title = 'No records found', description, children, asCell = false, colSpan = 1 }) {
  const content = <Stack spacing={0.75} alignItems="center" sx={{ py: 5, px: 2, color: 'text.secondary' }}><Typography variant="body2" fontWeight={650} color="text.primary">{title}</Typography>{description && <Typography variant="body2" align="center">{description}</Typography>}{children}</Stack>;
  if (asCell) return <TableCell colSpan={colSpan} align="center">{content}</TableCell>;
  return <Box sx={{ textAlign: 'center' }}>{content}</Box>;
}

/** One pagination control used by both layouts; callers own local/server paging semantics. */
export function TablePagination({ page, pageCount, pageSize, pageSizeOptions = [10, 25, 50, 100], total, disabled = false, onPageChange, onPageSizeChange, showRange = true }) {
  const safePage = Math.max(1, Number(page) || 1);
  const count = Math.max(1, Number(pageCount) || 1);
  const size = Math.max(1, Number(pageSize) || 25);
  const start = Number(total) > 0 ? (safePage - 1) * size + 1 : 0;
  const end = Math.min(safePage * size, Number(total) || 0);
  return <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
    <Stack direction="row" spacing={1.25} alignItems="center"><Typography variant="caption" color="text.secondary">{showRange ? `Showing ${start}–${end} of ${Number(total || 0).toLocaleString()}` : `Page ${safePage} of ${count}`}</Typography><TextField select size="small" label="Rows" value={size} onChange={(event) => onPageSizeChange?.(Number(event.target.value))} sx={{ width: 90 }} inputProps={{ 'aria-label': 'Rows per page' }} SelectProps={{ native: true }}>{pageSizeOptions.map((option) => <option key={option} value={option}>{option}</option>)}</TextField></Stack>
    <Stack direction="row" spacing={1}><Button size="small" disabled={disabled || safePage <= 1} onClick={() => onPageChange?.(safePage - 1)}>Previous</Button><Button size="small" disabled={disabled || safePage >= count} onClick={() => onPageChange?.(safePage + 1)}>Next</Button></Stack>
  </Stack>;
}

export const TABLE_HEADER_CELL_SX = {
  bgcolor: 'background.paper',
  color: 'text.secondary',
  fontSize: '0.75rem',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  borderBottom: '1px solid',
  borderColor: 'divider',
};
